import React, { useEffect, useState } from "react";
import { makeStyles } from "tss-react/mui";
import {
  Autocomplete,
  Button,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { HubOutlined, VisibilityOutlined, VisibilityOffOutlined } from "@mui/icons-material";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "../../services/toast";
import { i18n } from "../../translate/i18n";
import SettingsCard from "../Settings/SettingsCard";
import { setCachedMetaPlatform } from "../../hooks/useMetaGraphVersion";

const KNOWN_VERSIONS = [
  { version: "v26.0", released: "2026-07-29", expires: null },
  { version: "v25.0", released: "2026-02-18", expires: "2028-07-29" },
  { version: "v24.0", released: "2025-10-08", expires: "2028-02-18" },
  { version: "v23.0", released: "2025-05-29", expires: "2027-10-08" },
  { version: "v22.0", released: "2025-01-21", expires: "2027-05-20" },
  { version: "v21.0", released: "2024-10-02", expires: "2027-01-21" },
];

const VERSION_PATTERN = /^v\d{2}\.0$/;

const EMPTY_FORM = {
  version: "",
  appId: "",
  appSecret: "",
  whatsappAppId: "",
  whatsappAppSecret: "",
  whatsappConfigId: "",
};

const useStyles = makeStyles()((theme) => ({
  section: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
    paddingTop: theme.spacing(2),
    marginTop: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  firstSection: {
    borderTop: 0,
    marginTop: 0,
    paddingTop: 0,
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: 700,
  },
  fields: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: theme.spacing(1.5),
  },
  table: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    borderCollapse: "separate",
    overflow: "hidden",
    "& th": {
      fontSize: 11.5,
      fontWeight: 700,
      textTransform: "uppercase",
      color: theme.palette.text.secondary,
      background: theme.palette.action.hover,
    },
    "& tbody tr:last-of-type td": {
      borderBottom: 0,
    },
  },
  current: {
    fontWeight: 700,
    color: theme.palette.primary.main,
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: theme.spacing(2),
  },
}));

const formatDate = (value) =>
  value
    ? new Date(`${value}T12:00:00`).toLocaleDateString(i18n.language?.startsWith("en") ? "en-US" : "es-EC", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : i18n.t("metaGraphVersion.noExpiry");

const SecretField = ({ label, value, saved, onChange }) => {
  const [visible, setVisible] = useState(false);
  return (
    <TextField
      size="small"
      label={label}
      type={visible ? "text" : "password"}
      value={value}
      placeholder={saved ? i18n.t("metaGraphVersion.secretSaved") : ""}
      onChange={(e) => onChange(e.target.value)}
      slotProps={{
        inputLabel: saved ? { shrink: true } : undefined,
        input: {
          endAdornment: (
            <InputAdornment position="end">
              <IconButton size="small" onClick={() => setVisible((v) => !v)}>
                {visible ? <VisibilityOffOutlined fontSize="small" /> : <VisibilityOutlined fontSize="small" />}
              </IconButton>
            </InputAdornment>
          ),
        },
      }}
    />
  );
};

const MetaGraphVersionSettings = () => {
  const { classes, cx } = useStyles();
  const [saved, setSaved] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const applySaved = (data) => {
    setSaved(data);
    setForm({ ...EMPTY_FORM, ...data, appSecret: "", whatsappAppSecret: "" });
  };

  useEffect(() => {
    api
      .get("/meta-platform")
      .then(({ data }) => applySaved(data))
      .catch(toastError);
  }, []);

  const set = (field) => (value) => setForm((f) => ({ ...f, [field]: value }));

  const versionValid = VERSION_PATTERN.test(form.version.trim());

  const changed =
    saved &&
    (form.version.trim() !== saved.version ||
      form.appId.trim() !== (saved.appId || "") ||
      form.whatsappAppId.trim() !== (saved.whatsappAppId || "") ||
      form.whatsappConfigId.trim() !== (saved.whatsappConfigId || "") ||
      Boolean(form.appSecret.trim()) ||
      Boolean(form.whatsappAppSecret.trim()));

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/meta-platform", form);
      applySaved(data);
      setCachedMetaPlatform(data);
      toast.success(i18n.t("metaGraphVersion.saved"));
    } catch (err) {
      toastError(err);
    }
    setSaving(false);
  };

  return (
    <SettingsCard
      icon={HubOutlined}
      title={i18n.t("metaGraphVersion.title")}
      subtitle={i18n.t("metaGraphVersion.subtitle")}
    >
      <div className={cx(classes.section, classes.firstSection)}>
        <Typography className={classes.sectionTitle}>{i18n.t("metaGraphVersion.versionSection")}</Typography>
        <Autocomplete
          freeSolo
          size="small"
          sx={{ width: 220 }}
          options={KNOWN_VERSIONS.map((item) => item.version)}
          value={form.version}
          onInputChange={(_, value) => set("version")(value || "")}
          renderInput={(params) => (
            <TextField {...params} label={i18n.t("metaGraphVersion.version")} error={Boolean(form.version) && !versionValid} />
          )}
        />
        <Table size="small" className={classes.table}>
          <TableHead>
            <TableRow>
              <TableCell>{i18n.t("metaGraphVersion.version")}</TableCell>
              <TableCell>{i18n.t("metaGraphVersion.released")}</TableCell>
              <TableCell>{i18n.t("metaGraphVersion.expires")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {KNOWN_VERSIONS.map((item) => (
              <TableRow key={item.version}>
                <TableCell className={item.version === saved?.version ? classes.current : undefined}>
                  {item.version}
                  {item.version === saved?.version ? ` · ${i18n.t("metaGraphVersion.inUse")}` : ""}
                </TableCell>
                <TableCell>{formatDate(item.released)}</TableCell>
                <TableCell>{formatDate(item.expires)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className={classes.section}>
        <Typography className={classes.sectionTitle}>{i18n.t("metaGraphVersion.mainApp")}</Typography>
        <div className={classes.fields}>
          <TextField size="small" label={i18n.t("metaGraphVersion.appId")} value={form.appId} onChange={(e) => set("appId")(e.target.value)} />
          <SecretField
            label={i18n.t("metaGraphVersion.appSecret")}
            value={form.appSecret}
            saved={saved?.hasAppSecret}
            onChange={set("appSecret")}
          />
        </div>
      </div>

      <div className={classes.section}>
        <Typography className={classes.sectionTitle}>{i18n.t("metaGraphVersion.whatsappApp")}</Typography>
        <div className={classes.fields}>
          <TextField
            size="small"
            label={i18n.t("metaGraphVersion.appId")}
            value={form.whatsappAppId}
            onChange={(e) => set("whatsappAppId")(e.target.value)}
          />
          <SecretField
            label={i18n.t("metaGraphVersion.appSecret")}
            value={form.whatsappAppSecret}
            saved={saved?.hasWhatsappAppSecret}
            onChange={set("whatsappAppSecret")}
          />
          <TextField
            size="small"
            label={i18n.t("metaGraphVersion.configId")}
            value={form.whatsappConfigId}
            onChange={(e) => set("whatsappConfigId")(e.target.value)}
          />
        </div>
      </div>

      <div className={classes.actions}>
        <Button variant="contained" disableElevation disabled={saving || !versionValid || !changed} onClick={handleSave}>
          {i18n.t("metaGraphVersion.save")}
        </Button>
      </div>
    </SettingsCard>
  );
};

export default MetaGraphVersionSettings;
